/**
 * MIGRATION DATA ACCESS LAYER
 * Production-ready data access layer using companyUsers collection only
 * Phase 3: Removed legacy/dual-mode code - single source of truth
 */

import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  query, 
  where, 
  setDoc, 
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { FIRESTORE_COLLECTIONS, COMPANY_USER_LIVE_FIELD_KEYS } from '../constants/firestoreCollections';
import { COMPANY_USER_PARTNERS_SUBCOLLECTION } from '../constants/companyUserFirestoreSecurity';
import {
  mergeCompanyUserLiveIntoSnapshot,
  fetchCompanyUserLiveMap,
} from '../utils/companyUserLiveFirestore';
import { recordPerformance as monitorPerformance, recordError as monitorError } from './monitoringService';
import Logger from './logger';
import {
  fetchTranzitCarrierDirectoryList,
  fetchTranzitUserProfileViaApi
} from './tranzitCarrierDirectoryApi';

// Migration mode types (Phase 3: Only 'new' mode supported)
export type MigrationMode = 'new';
export type DataAccessResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
  source: 'new' | 'fallback';
};

// User profile interface
export interface UserProfile {
  id: string;
  email: string;
  companyName: string;
  companyRep: string;
  phoneNumber?: string;
  userType: 'shipper' | 'carrier' | 'broker' | 'broker_carrier' | 'admin';
  role: string;
  status: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  [key: string]: any;
}

// Partner interface
export interface Partner {
  id: string;
  companyName: string;
  companyRep?: string;
  phoneNumber?: string;
  email?: string;
  userType?: 'shipper' | 'broker' | 'carrier';
  addedAt?: any;
  [key: string]: any;
}

// Carrier interface
export interface Carrier {
  id: string;
  companyName: string;
  companyRep?: string;
  phoneNumber?: string;
  email?: string;
  userType: 'carrier';
  status: string;
  [key: string]: any;
}

/**
 * SAFE DATA ACCESS LAYER
 * Phase 3: Simplified to use companyUsers collection only
 */
export class MigrationDataAccessLayer {
  private performanceMetrics: Map<string, number> = new Map();
  private errorCount: Map<string, number> = new Map();

  constructor() {
    this.initializeErrorTracking();
  }

  /**
   * Get current migration mode (always 'new' in Phase 3)
   */
  getMigrationMode(): MigrationMode {
    return 'new';
  }

  /**
   * SAFE USER PROFILE ACCESS
   * Phase 3: Simplified to use companyUsers collection only
   */
  async getUserProfile(userId: string): Promise<DataAccessResult<UserProfile>> {
    const startTime = Date.now();
    
    try {
      return await this.getNewUserProfile(userId);
    } catch (error) {
      this.recordError('getUserProfile', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    } finally {
      this.recordPerformance('getUserProfile', Date.now() - startTime);
    }
  }

  /**
   * SAFE PARTNER MANAGEMENT
   * Phase 3: Simplified to use companyUsers collection only
   */
  async getPartners(userId: string): Promise<DataAccessResult<Partner[]>> {
    const startTime = Date.now();
    
    try {
      return await this.getNewPartners(userId);
    } catch (error) {
      this.recordError('getPartners', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    } finally {
      this.recordPerformance('getPartners', Date.now() - startTime);
    }
  }

  /**
   * SAFE CARRIER DIRECTORY
   * Phase 3: Simplified to use companyUsers collection only
   */
  async getCarriers(): Promise<DataAccessResult<Carrier[]>> {
    const startTime = Date.now();
    
    try {
      return await this.getNewCarriers();
    } catch (error) {
      this.recordError('getCarriers', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    } finally {
      this.recordPerformance('getCarriers', Date.now() - startTime);
    }
  }

  /**
   * SAFE PARTNER OPERATIONS
   * Phase 3: Simplified to use companyUsers collection only
   */
  async addPartner(userId: string, partnerId: string, partnerData: Partner): Promise<DataAccessResult<boolean>> {
    try {
      return await this.addNewPartner(userId, partnerId, partnerData);
    } catch (error) {
      this.recordError('addPartner', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    }
  }

  /**
   * SAFE PARTNER REMOVAL
   * Phase 3: Simplified to use companyUsers collection only
   */
  async removePartner(userId: string, partnerId: string): Promise<DataAccessResult<boolean>> {
    try {
      return await this.removeNewPartner(userId, partnerId);
    } catch (error) {
      this.recordError('removePartner', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    }
  }

  /**
   * SAFE PARTNERSHIP CHECK
   * Phase 3: Simplified to use companyUsers collection only
   */
  async checkPartnership(userId: string, partnerId: string): Promise<DataAccessResult<{ exists: boolean; data?: any }>> {
    try {
      return await this.checkNewPartnership(userId, partnerId);
    } catch (error) {
      this.recordError('checkPartnership', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    }
  }

  /**
   * SAFE USER PROFILE UPDATE
   * Phase 3: Simplified to use companyUsers collection only
   */
  async updateUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<DataAccessResult<boolean>> {
    try {
      return await this.updateNewUserProfile(userId, profileData);
    } catch (error) {
      this.recordError('updateUserProfile', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'fallback'
      };
    }
  }

  // ===== NEW COLLECTION ACCESS METHODS =====
  // Phase 3: Only companyUsers collection is used

  private async getNewUserProfile(userId: string): Promise<DataAccessResult<UserProfile>> {
    try {
      const auth = getFailoverAwareAuth();
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid !== userId) {
        const token = await currentUser.getIdToken();
        const apiResult = await fetchTranzitUserProfileViaApi(token, userId);
        if (apiResult.success && apiResult.profile) {
          return {
            success: true,
            data: { ...apiResult.profile, id: userId } as UserProfile,
            source: 'new'
          };
        }
        return {
          success: false,
          error: apiResult.error || 'Profile not available',
          source: 'new'
        };
      }

      const db = getFailoverAwareDatabase();
      Logger.debug(`[MigrationDataAccessLayer] Fetching from companyUsers collection for user: ${userId}`);
      const userDoc = await getDoc(doc(db, FIRESTORE_COLLECTIONS.COMPANY_USERS, userId));
      if (!userDoc.exists()) {
        Logger.debug(`[MigrationDataAccessLayer] User not found in companyUsers collection: ${userId}`);
        return {
          success: false,
          error: 'User not found in new collection',
          source: 'new'
        };
      }

      const liveSnap = await getDoc(doc(db, FIRESTORE_COLLECTIONS.COMPANY_USER_LIVE, userId));
      const liveData = liveSnap.exists() ? (liveSnap.data() as Record<string, unknown>) : null;

      // Get document data and add id field (Firestore doesn't include it in data())
      const docData = mergeCompanyUserLiveIntoSnapshot(
        userDoc.data() as Record<string, unknown>,
        liveData
      );
      const dataWithId = {
        id: userId, // Add id FIRST so it can't be overwritten
        ...docData
      } as UserProfile;
      
      // Log what we actually got for debugging
      const dataKeys = Object.keys(docData || {});
      Logger.debug(`[MigrationDataAccessLayer] Successfully fetched from companyUsers collection: ${userId}`, {
        fieldCount: dataKeys.length,
        fields: dataKeys,
        sampleFields: {
          companyName: docData?.companyName,
          email: docData?.email,
          status: docData?.status
        },
        dataWithId: {
          id: dataWithId.id,
          companyName: dataWithId.companyName,
          email: dataWithId.email
        }
      });
      
      return {
        success: true,
        data: dataWithId,
        source: 'new'
      };
    } catch (error) {
      Logger.error(`[MigrationDataAccessLayer] Error fetching from companyUsers collection: ${userId}`, error as Error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }

  private async getNewPartners(userId: string): Promise<DataAccessResult<Partner[]>> {
    try {
      const db = getFailoverAwareDatabase();
      const partnersSnapshot = await getDocs(
        collection(db, FIRESTORE_COLLECTIONS.COMPANY_USERS, userId, COMPANY_USER_PARTNERS_SUBCOLLECTION)
      );
      const partners = partnersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Partner))
        .filter(partner => partner.id !== userId); // Filter out self from partner list
      
      return {
        success: true,
        data: partners,
        source: 'new'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }

  private async getNewCarriers(): Promise<DataAccessResult<Carrier[]>> {
    try {
      const auth = getFailoverAwareAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { success: false, error: 'Not authenticated', source: 'new' };
      }
      const token = await currentUser.getIdToken();
      const apiResult = await fetchTranzitCarrierDirectoryList(token);
      if (!apiResult.success || !apiResult.carriers) {
        return {
          success: false,
          error: apiResult.error || 'Failed to load carriers',
          source: 'new'
        };
      }
      const carriers = apiResult.carriers.map(
        (row) =>
          ({
            ...row,
            userType: 'carrier' as const
          }) as Carrier
      );

      return {
        success: true,
        data: carriers,
        source: 'new'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }

  // Phase 3: Dual mode and legacy methods removed - only using companyUsers collection

  // ===== PARTNER OPERATION METHODS =====

  private async addNewPartner(userId: string, partnerId: string, partnerData: Partner): Promise<DataAccessResult<boolean>> {
    try {
      const db = getFailoverAwareDatabase();
      const partnerRef = doc(
        db,
        FIRESTORE_COLLECTIONS.COMPANY_USERS,
        userId,
        COMPANY_USER_PARTNERS_SUBCOLLECTION,
        partnerId
      );
      await setDoc(partnerRef, partnerData);
      return { success: true, data: true, source: 'new' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }


  private async removeNewPartner(userId: string, partnerId: string): Promise<DataAccessResult<boolean>> {
    try {
      const db = getFailoverAwareDatabase();
      const partnerRef = doc(
        db,
        FIRESTORE_COLLECTIONS.COMPANY_USERS,
        userId,
        COMPANY_USER_PARTNERS_SUBCOLLECTION,
        partnerId
      );
      await deleteDoc(partnerRef);
      return { success: true, data: true, source: 'new' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }


  private async checkNewPartnership(userId: string, partnerId: string): Promise<DataAccessResult<{ exists: boolean; data?: any }>> {
    try {
      const db = getFailoverAwareDatabase();
      const partnerRef = doc(
        db,
        FIRESTORE_COLLECTIONS.COMPANY_USERS,
        userId,
        COMPANY_USER_PARTNERS_SUBCOLLECTION,
        partnerId
      );
      const partnerDoc = await getDoc(partnerRef);
      return {
        success: true,
        data: {
          exists: partnerDoc.exists(),
          data: partnerDoc.exists() ? partnerDoc.data() : undefined
        },
        source: 'new'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }

  // ===== USER PROFILE UPDATE METHODS =====
  // Phase 3: Legacy update method removed - only using companyUsers collection

  private isCompanyUserLiveFieldKey(key: string): boolean {
    return (COMPANY_USER_LIVE_FIELD_KEYS as readonly string[]).includes(key);
  }

  private async updateNewUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<DataAccessResult<boolean>> {
    try {
      Logger.debug('[MigrationDataAccessLayer] updateNewUserProfile called with:', { userId, profileData });

      const db = getFailoverAwareDatabase();
      const userRef = doc(db, FIRESTORE_COLLECTIONS.COMPANY_USERS, userId);
      const userDoc = await getDoc(userRef);
      const documentExists = userDoc.exists();

      const hasNestedFields = Object.keys(profileData).some((key) => key.includes('.'));
      Logger.debug('[MigrationDataAccessLayer] hasNestedFields:', { hasNestedFields, documentExists });

      if (hasNestedFields) {
        if (!documentExists) {
          Logger.warn('[MigrationDataAccessLayer] Document does not exist, cannot use updateDoc for nested fields');
          return {
            success: false,
            error: 'Document does not exist',
            source: 'new'
          };
        }
        await updateDoc(userRef, profileData);
        Logger.debug('[MigrationDataAccessLayer] updateNewUserProfile completed successfully');
        return { success: true, data: true, source: 'new' };
      }

      const livePayload: Record<string, unknown> = {};
      const profilePayload: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(profileData)) {
        if (value === undefined) continue;
        if (this.isCompanyUserLiveFieldKey(key)) {
          livePayload[key] = value;
        } else {
          profilePayload[key] = value;
        }
      }

      const writes: Promise<void>[] = [];

      if (Object.keys(livePayload).length > 0) {
        const liveRef = doc(db, FIRESTORE_COLLECTIONS.COMPANY_USER_LIVE, userId);
        writes.push(
          setDoc(liveRef, livePayload as Record<string, any>, { merge: true }).then(() => undefined)
        );
      }

      if (Object.keys(profilePayload).length > 0) {
        const profileWrite = profilePayload as Record<string, any>;
        if (documentExists) {
          writes.push(updateDoc(userRef, profileWrite).then(() => undefined));
        } else {
          writes.push(setDoc(userRef, profileWrite, { merge: true }).then(() => undefined));
        }
      }

      if (writes.length === 0) {
        return { success: true, data: true, source: 'new' };
      }

      await Promise.all(writes);
      Logger.debug('[MigrationDataAccessLayer] updateNewUserProfile completed successfully');
      return { success: true, data: true, source: 'new' };
    } catch (error) {
      Logger.error('[MigrationDataAccessLayer] updateNewUserProfile error:', error as Error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'new'
      };
    }
  }

  // ===== SAFETY AND VALIDATION METHODS =====
  // Phase 3: Mode transition validation removed - only 'new' mode supported

  private async performHealthCheck(): Promise<boolean> {
    try {
      // Test critical data access points
      const testUserId = 'test-user-id';
      
      // Test user profile access
      const profileTest = await this.getUserProfile(testUserId);
      
      // Test partner access
      const partnerTest = await this.getPartners(testUserId);
      
      // Test carrier access
      const carrierTest = await this.getCarriers();
      
      // All tests should either succeed or fail gracefully
      return true; // Health check passed
    } catch (error) {
      Logger.error('[MigrationDataAccessLayer] Health check failed:', error as Error);
      return false;
    }
  }

  private initializeErrorTracking(): void {
    this.errorCount.set('getUserProfile', 0);
    this.errorCount.set('getPartners', 0);
    this.errorCount.set('getCarriers', 0);
    this.errorCount.set('addPartner', 0);
    this.errorCount.set('updateUserProfile', 0);
  }

  private recordError(operation: string, error: any): void {
    // Also record in monitoring service (Phase 2: Monitoring)
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    monitorError(`[MigrationDataAccessLayer] ${operation}`, errorStack, { operation, error: errorMessage });
    const currentCount = this.errorCount.get(operation) || 0;
    this.errorCount.set(operation, currentCount + 1);
    
    Logger.error(`[MigrationDataAccessLayer] Error in ${operation}:`, error as Error);
  }

  private recordPerformance(operation: string, duration: number): void {
    // Also record in monitoring service (Phase 2: Monitoring)
    monitorPerformance(`[MigrationDataAccessLayer] ${operation}`, duration, true);
    this.performanceMetrics.set(operation, duration);
    
    if (duration > 1000) {
      Logger.warn(`[MigrationDataAccessLayer] Slow operation ${operation}: ${duration}ms`);
    }
  }

  // ===== MONITORING AND METRICS =====

  getPerformanceMetrics(): Map<string, number> {
    return new Map(this.performanceMetrics);
  }

  getErrorCounts(): Map<string, number> {
    return new Map(this.errorCount);
  }

  getSystemHealth(): { healthy: boolean; metrics: any } {
    const errorCounts = Array.from(this.errorCount.values());
    const totalErrors = errorCounts.reduce((sum, count) => sum + count, 0);
    
    const performanceValues = Array.from(this.performanceMetrics.values());
    const avgPerformance = performanceValues.length > 0 
      ? performanceValues.reduce((sum, time) => sum + time, 0) / performanceValues.length 
      : 0;
    
    return {
      healthy: totalErrors === 0 && avgPerformance < 2000,
      metrics: {
        totalErrors,
        avgPerformance,
        migrationMode: 'new' // Phase 3: Always 'new'
      }
    };
  }
}

// Export singleton instance
export const migrationDataAccess = new MigrationDataAccessLayer();
